import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, type TeamMember } from '../api';
import { businessesApi, type BusinessListItem } from '../api/businesses';
import { overviewApi, type OverviewData, type Ticket, type TicketMove } from '../api/overview';
import { Table, type Column } from '../components/Table';
import { t } from '../i18n';
import { Link } from '../router';
import { OUTCOME_KEY, stepText } from './SkillAgents';
import { fmtWhen } from './tasks.format';
import './overview.css';

const LANGUAGES = ['uk', 'en'];
const CURRENCIES = ['EUR', 'UAH', 'USD'];
const TIMEZONES = ['Europe/Lisbon', 'Europe/Kyiv', 'Europe/Warsaw', 'UTC'];
const ON_OLENA_SHOWN = 10;

function statusLabel(status: string): string {
  if (status === 'active') return t('overview.status.active');
  if (status === 'paused') return t('overview.status.paused');
  return t('overview.status.archived');
}

const ticketStatus = (s: string): string => {
  const label = t(`overview.sviteya.status.${s}` as Parameters<typeof t>[0]);
  return label.startsWith('overview.') ? s : label;
};

const dm = (iso: string) => new Date(iso).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit' });
const hm = (iso: string) => new Date(iso).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
const ticketUrl = (base: string | null, code: string) => (base ? `${base}/admin/tasks?task=${encodeURIComponent(code)}` : null);

/** The time part of a calendar start: "2026-10-20T10:00" → "10:00"; an all-day event has none. */
function eventTime(start: string, allDay?: boolean): string {
  if (allDay || !start.includes('T')) return t('overview.today.allDay');
  return start.slice(11, 16);
}

/** «sviteya=connected» after Sviteya sent the human back; read once, then removed from the address. */
function landingFromUrl(): 'connected' | 'denied' | 'error' | null {
  const v = new URLSearchParams(window.location.search).get('sviteya');
  return v === 'connected' || v === 'denied' || v === 'error' ? v : null;
}

/**
 * «Огляд» (ADR-0009, foundation section 7): the page the human opens in the morning instead of a
 * notebook. Top to bottom: what waits for her, today, dialogs, stuck tasks, the Sviteya board,
 * the inbox, knowledge (step 5) and the directions — where businesses are also managed.
 */
export function Overview({ canManage, canWriteTasks, canConnect }: { canManage: boolean; canWriteTasks: boolean; canConnect: boolean }) {
  const [data, setData] = useState<OverviewData | null>(null);
  const [businesses, setBusinesses] = useState<BusinessListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [landing] = useState(landingFromUrl);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);

  const reload = () => {
    overviewApi.get().then(setData).catch((e: Error) => setError(e.message));
    businessesApi.list().then(setBusinesses).catch((e: Error) => setError(e.message));
  };

  useEffect(() => {
    if (landing) window.history.replaceState({}, '', '/');
    reload();
  }, [landing]);

  const editing = editingId && editingId !== 'new' ? businesses.find((b) => b.id === editingId) ?? null : null;

  return (
    <>
      <h1>{t('nav.overview')}</h1>
      <p className="muted">{data ? t('overview.intro', { time: hm(data.generatedAt) }) : t('common.loading')}</p>
      {error && <div className="error">{error}</div>}
      {landing === 'connected' && <div className="note" style={{ marginBottom: 'var(--sp-3)' }}>{t('overview.sviteya.connected')}</div>}
      {landing === 'denied' && <div className="note" style={{ marginBottom: 'var(--sp-3)' }}>{t('overview.sviteya.denied')}</div>}
      {landing === 'error' && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{t('overview.sviteya.failed')}</div>}

      {data && (
        <div className="dash">
          <Waiting data={data} />
          <div className="grid2">
            <Today data={data} />
            <Dialogs data={data} />
          </div>
          {data.agents && <AgentsBlock agents={data.agents} />}
          <Stuck data={data} />
          <Sviteya data={data} canConnect={canConnect} businesses={businesses} />
          <div className="grid2">
            <Inbox data={data} />
            {data.knowledge && <KnowledgeWeekBlock week={data.knowledge} />}
          </div>
          <Directions data={data} canManage={canManage} canWriteTasks={canWriteTasks} onSettings={setEditingId} onChanged={reload} />
        </div>
      )}

      {editingId && (
        <>
          <h2>{editing ? t('overview.settings.title', { name: editing.name }) : t('overview.settings.newTitle')}</h2>
          <BusinessForm
            key={editingId ?? 'none'}
            business={editing}
            onSaved={() => {
              setEditingId(null);
              reload();
            }}
            onCancel={() => setEditingId(null)}
          />
        </>
      )}
    </>
  );
}

function Waiting({ data }: { data: OverviewData }) {
  const groups = useMemo(() => {
    const map = new Map<string, { name: string; rows: OverviewData['waiting'] }>();
    for (const w of data.waiting) {
      const g = map.get(w.business.id) ?? { name: w.business.name, rows: [] };
      g.rows.push(w);
      map.set(w.business.id, g);
    }
    return [...map.values()];
  }, [data.waiting]);
  const sv = data.sviteya;
  const onOlena = sv?.connected ? sv.onOlena : [];
  const total = data.waiting.length + onOlena.length;

  return (
    <section className="card">
      <h2>
        {t('overview.waiting.title')} {total > 0 && <span className="badge warn">{total}</span>}
      </h2>
      {total === 0 && <p className="muted" style={{ margin: 0 }}>{t('overview.waiting.none')}</p>}
      {groups.map((g) => (
        <div key={g.name}>
          <h3>
            {g.name} · {g.rows.length}
          </h3>
          <ul className="dash-list">
            {g.rows.map((w) => (
              <li key={w.id}>
                <span className="key">{w.key}</span>
                <Link to={`/tasks/${w.id}`}>{w.title}</Link>
                <span className="meta">
                  {w.from?.kind === 'dialog' && (
                    <>
                      <Link to={`/dialogs?id=${encodeURIComponent(w.from.id)}`}>{t('overview.waiting.fromDialog', { title: w.from.title })}</Link> ·{' '}
                    </>
                  )}
                  {w.from?.kind === 'agent' && (
                    <>
                      <Link to={`/agents?agent=${encodeURIComponent(w.from.id)}`}>{t('overview.waiting.fromAgent', { title: w.from.title })}</Link> ·{' '}
                    </>
                  )}
                  {w.from?.kind === 'inbox' && (
                    <>
                      <Link to={`/inbox?id=${encodeURIComponent(w.from.id)}`}>{t('overview.waiting.fromInbox', { title: w.from.title })}</Link> ·{' '}
                    </>
                  )}
                  {fmtWhen(w.createdAt, false)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {onOlena.length > 0 && (
        <>
          <h3>
            {t('overview.waiting.sviteya')} · {onOlena.length}
          </h3>
          <TicketList tickets={onOlena.slice(0, ON_OLENA_SHOWN)} base={sv!.baseUrl} meta={(tk) => `${t('overview.waiting.sviteyaSince', { date: dm(tk.since) })}${tk.area ? ` · ${tk.area}` : ''}`} />
          {onOlena.length > ON_OLENA_SHOWN && <p className="muted" style={{ margin: 'var(--sp-2) 0 0' }}>{t('overview.waiting.more', { n: onOlena.length - ON_OLENA_SHOWN })}</p>}
        </>
      )}
    </section>
  );
}

function TicketList({ tickets, base, meta }: { tickets: Ticket[]; base: string | null; meta: (t: Ticket) => string }) {
  return (
    <ul className="dash-list">
      {tickets.map((tk) => {
        const href = ticketUrl(base, tk.code);
        return (
          <li key={tk.id}>
            <span className="key">{tk.code}</span>
            {href ? (
              <a href={href} target="_blank" rel="noreferrer">{tk.title}</a>
            ) : (
              <span>{tk.title}</span>
            )}
            <span className="meta">{meta(tk)}</span>
          </li>
        );
      })}
    </ul>
  );
}

function Today({ data }: { data: OverviewData }) {
  const { calendar, deadlines, nowCount, date } = data.today;
  const title = new Date(`${date}T12:00:00`).toLocaleDateString('uk-UA', { weekday: 'long', day: 'numeric', month: 'long' });
  return (
    <section className="card">
      <h2>
        {t('overview.today.title')} <span className="muted">{title}</span>
      </h2>
      {!calendar && <p className="muted" style={{ marginTop: 0 }}>{t('overview.today.noCalendar')}</p>}
      {calendar && calendar.events.length === 0 && <p className="muted" style={{ marginTop: 0 }}>{t('overview.today.noEvents')}</p>}
      {calendar && calendar.events.length > 0 && (
        <ul className="dash-list">
          {calendar.events.map((e, i) => (
            <li key={`${e.start}-${i}`}>
              <span className="key">{eventTime(e.start, e.allDay)}</span>
              {e.url ? (
                <a href={e.url} target="_blank" rel="noreferrer">{e.title}</a>
              ) : (
                <span>{e.title}</span>
              )}
              {e.location && <span className="meta">{e.location}</span>}
            </li>
          ))}
        </ul>
      )}
      {deadlines.length > 0 && (
        <>
          <h3>{t('overview.today.deadlines')}</h3>
          <ul className="dash-list">
            {deadlines.map((d) => (
              <li key={d.id}>
                <span className="key">{d.key}</span>
                <Link to={`/tasks/${d.id}`}>{d.title}</Link>
                <span className="meta">
                  {d.overdue && <span className="badge danger">{t('overview.today.overdue')}</span>} {dm(d.dueAt)} · {d.business.name}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="muted" style={{ margin: 'var(--sp-3) 0 0' }}>
        {t('overview.today.queueNow', { n: nowCount })} · <Link to="/tasks">{t('overview.today.allTasks')}</Link>
      </p>
    </section>
  );
}

function Dialogs({ data }: { data: OverviewData }) {
  const c = data.dialogs.counts;
  return (
    <section className="card">
      <h2>{t('overview.dialogs.title')}</h2>
      <p className="muted" style={{ marginTop: 0 }}>{t('overview.dialogs.counts', { working: c.working, waiting: c.waiting_human, stale: c.stale })}</p>
      {data.dialogs.rows.length === 0 && <p className="muted" style={{ margin: 0 }}>{t('overview.dialogs.none')}</p>}
      <ul className="dash-list">
        {data.dialogs.rows.map((d) => (
          <li key={d.id}>
            <span className={d.state === 'stale' ? 'badge danger' : 'badge warn'}>{t(`dialogs.status.${d.state}`)}</span>
            <Link to={`/dialogs?id=${encodeURIComponent(d.id)}`}>{d.title}</Link>
            <span className="meta">
              {t(`dialogs.tool.${d.tool}`)}
              {d.business ? ` · ${d.business.name}` : ''} · {fmtWhen(d.lastActivityAt)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** «Агенти» (ADR-0011): each skill agent — its step, what it is on, its last run, what waits for you. */
function AgentsBlock({ agents }: { agents: NonNullable<OverviewData['agents']> }) {
  return (
    <section className="card">
      <h2>{t('overview.agents.title')}</h2>
      {agents.length === 0 && <p className="muted" style={{ margin: 0 }}>{t('overview.agents.none')}</p>}
      <ul className="dash-list">
        {agents.map((a) => (
          <li key={a.id}>
            <Link to={`/agents?agent=${encodeURIComponent(a.slug)}`}>{a.name}</Link>
            <span>
              {stepText(a.playbookStep)}
              {a.questions > 0 && (
                <>
                  {' '}
                  <span className="badge warn">{t('overview.agents.questions', { n: a.questions })}</span>
                </>
              )}
            </span>
            <span className="meta">
              {a.currentTask ? (
                <>
                  {t('overview.agents.on', { key: a.currentTask.key })}{' '}
                  <Link to={`/tasks/${a.currentTask.id}`}>{a.currentTask.title}</Link>
                </>
              ) : (
                t('overview.agents.idle')
              )}
              {' · '}
              {a.lastRun ? t('overview.agents.last', { when: fmtWhen(a.lastRun.finishedAt), outcome: t(OUTCOME_KEY[a.lastRun.outcome]) }) : t('skillAgents.neverRan')}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Stuck({ data }: { data: OverviewData }) {
  return (
    <section className="card">
      <h2>
        {t('overview.stuck.title')} <span className="muted">{t('overview.stuck.hint')}</span>
      </h2>
      {data.stuck.length === 0 && <p className="muted" style={{ margin: 0 }}>{t('overview.stuck.none')}</p>}
      <ul className="dash-list">
        {data.stuck.map((s) => (
          <li key={s.id}>
            <span className="key">{s.key}</span>
            <Link to={`/tasks/${s.id}`}>{s.title}</Link>
            <span className="meta">
              {t('overview.stuck.meta', { days: s.days, date: dm(s.lastMoveAt) })} · {s.business.name}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Sviteya({ data, canConnect, businesses }: { data: OverviewData; canConnect: boolean; businesses: BusinessListItem[] }) {
  const sv = data.sviteya;
  const columns = useMemo<Column<TicketMove>[]>(
    () => [
      {
        key: 'code',
        title: t('overview.sviteya.col.code'),
        sortValue: (m) => m.code,
        render: (m) => {
          const href = ticketUrl(sv?.baseUrl ?? null, m.code);
          return href ? <a href={href} target="_blank" rel="noreferrer">{m.code}</a> : m.code;
        },
      },
      { key: 'title', title: t('overview.sviteya.col.title'), wrap: true, render: (m) => m.title },
      { key: 'move', title: t('overview.sviteya.col.move'), render: (m) => `${ticketStatus(m.from)} → ${ticketStatus(m.to)}` },
      { key: 'at', title: t('overview.sviteya.col.at'), sortValue: (m) => m.at, render: (m) => <span className="muted">{fmtWhen(m.at)}</span> },
    ],
    [sv?.baseUrl],
  );

  if (!sv?.connected) return <ConnectSviteya canConnect={canConnect} businesses={businesses} defaultBusinessId={sv?.businessId} reconnect={!!sv} />;
  return (
    <section className="card">
      <h2>
        {t('overview.sviteya.title')} <span className="muted">{t('overview.sviteya.hint', { when: sv.lastSyncAt ? fmtWhen(sv.lastSyncAt) : t('overview.sviteya.never') })}</span>
      </h2>
      {sv.lastError && <p className="error">{t('overview.sviteya.error', { error: sv.lastError })}</p>}
      <div className="kpi">
        <div>
          <span className="muted">{t('overview.sviteya.kpi.moved')}</span>
          <strong>{sv.moved.length}</strong>
        </div>
        <div>
          <span className="muted">{t('overview.sviteya.kpi.created')}</span>
          <strong>{sv.created.length}</strong>
        </div>
        <div>
          <span className="muted">{t('overview.sviteya.kpi.review')}</span>
          <strong>{sv.counts.review ?? 0}</strong>
        </div>
        <div>
          <span className="muted">{t('overview.sviteya.kpi.onOlena')}</span>
          <strong>{sv.onOlena.length}</strong>
        </div>
      </div>
      <h3>{t('overview.sviteya.moved')}</h3>
      <Table columns={columns} rows={sv.moved} rowKey={(m) => `${m.id}-${m.at}`} empty={t('overview.sviteya.movedNone')} defaultSort={{ key: 'at', dir: 'desc' }} />
      {sv.longestInReview.length > 0 && (
        <>
          <h3>{t('overview.sviteya.longest')}</h3>
          <TicketList tickets={sv.longestInReview} base={sv.baseUrl} meta={(tk) => `${t('overview.sviteya.reviewSince', { date: dm(tk.since) })}${tk.area ? ` · ${tk.area}` : ''}`} />
        </>
      )}
    </section>
  );
}

function ConnectSviteya({ canConnect, businesses, defaultBusinessId, reconnect }: { canConnect: boolean; businesses: BusinessListItem[]; defaultBusinessId?: string; reconnect: boolean }) {
  const guess = defaultBusinessId ?? businesses.find((b) => /sviteya/i.test(b.name))?.id ?? businesses[0]?.id ?? '';
  const [businessId, setBusinessId] = useState(guess);
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setBusinessId((cur) => cur || guess), [guess]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { authorizeUrl } = await overviewApi.connectSviteya(businessId, address);
      window.location.assign(authorizeUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>{t('overview.sviteya.connectTitle')}</h2>
      <p className="muted" style={{ marginTop: 0 }}>{t('overview.sviteya.connectText')}</p>
      {canConnect && (
        <form className="row" onSubmit={submit}>
          <label className="field" style={{ flex: '2 1 220px' }}>
            {t('overview.sviteya.address')}
            <input className="input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="app.example.com" required inputMode="url" autoCapitalize="none" />
          </label>
          <label className="field" style={{ flex: '1 1 160px' }}>
            {t('overview.sviteya.business')}
            <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} required>
              {businesses.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </label>
          <button className="btn" type="submit" disabled={busy || !businessId} style={{ alignSelf: 'flex-end' }}>
            {reconnect ? t('overview.sviteya.reconnect') : t('overview.sviteya.connect')}
          </button>
        </form>
      )}
      {error && <div className="error">{error}</div>}
    </section>
  );
}

function Inbox({ data }: { data: OverviewData }) {
  const c = data.inbox.counts;
  return (
    <section className="card">
      <h2>
        <Link to="/inbox">{t('overview.inbox.title')}</Link>
      </h2>
      <p className="muted" style={{ marginTop: 0 }}>{t('overview.inbox.counts', { new: c.new, needs: c.needs_human, sorted: c.sortedToday })}</p>
      <ul className="dash-list">
        {data.inbox.needsHuman.map((i) => (
          <li key={i.id}>
            <span className="badge warn">{t('overview.inbox.you')}</span>
            <Link to={`/inbox?id=${encodeURIComponent(i.id)}`}>{i.title ?? t(`inbox.source.${i.source}` as Parameters<typeof t>[0])}</Link>
            {i.triageNote && <span className="meta">{i.triageNote}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}

function KnowledgeWeekBlock({ week }: { week: NonNullable<OverviewData['knowledge']> }) {
  const c = week.counts;
  const empty = c.findings + c.funnels + c.sources === 0;
  return (
    <section className="card">
      <h2>
        <Link to="/knowledge">{t('overview.knowledge.title')}</Link>
      </h2>
      <p className="muted" style={{ marginTop: 0 }}>{empty ? t('overview.knowledge.none') : t('overview.knowledge.counts', { findings: c.findings, proposed: c.proposed, funnels: c.funnels, sources: c.sources })}</p>
      <ul className="dash-list">
        {week.findings.map((f) => (
          <li key={f.id}>
            <span className={f.status === 'proposed' ? 'badge warn' : 'badge'}>{t(`knowledge.kind.${f.kind}`)}</span>
            <Link to={`/knowledge?finding=${encodeURIComponent(f.id)}`}>«{f.text}»</Link>
            <span className="meta">{[f.status === 'proposed' ? t('knowledge.status.proposed') : null, f.segment, f.source?.title ?? f.funnelCase?.title].filter(Boolean).join(' · ')}</span>
          </li>
        ))}
        {week.funnels.map((f) => (
          <li key={f.id}>
            <span className="badge">{t('overview.knowledge.funnel')}</span>
            <Link to={`/knowledge?funnel=${encodeURIComponent(f.id)}`}>{f.title}</Link>
            <span className="meta">{[t('overview.knowledge.steps', { n: f.stepCount }), f.price].filter(Boolean).join(' · ')}</span>
          </li>
        ))}
        {week.sources.map((s) => (
          <li key={s.id}>
            <span className="badge">{t(`knowledge.sourceKind.${s.kind}`)}</span>
            <Link to={`/knowledge?source=${encodeURIComponent(s.id)}`}>{s.title}</Link>
            <span className="meta">{t(`knowledge.state.${s.state}`)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

type Direction = OverviewData['directions'][number];
type DirectionRow = Direction['projects'][number];

function Directions({ data, canManage, canWriteTasks, onSettings, onChanged }: { data: OverviewData; canManage: boolean; canWriteTasks: boolean; onSettings: (id: string | 'new') => void; onChanged: () => void }) {
  const columns = useMemo<Column<DirectionRow>[]>(
    () => [
      { key: 'project', title: t('overview.directions.col.project'), sortValue: (p) => p.name ?? '', render: (p) => p.name ?? <span className="muted">{t('overview.directions.noProject')}</span> },
      { key: 'now', title: t('overview.directions.col.now'), sortValue: (p) => p.now, render: (p) => <span className="num">{p.now}</span> },
      { key: 'next', title: t('overview.directions.col.next'), sortValue: (p) => p.next, render: (p) => <span className="num">{p.next}</span> },
      { key: 'later', title: t('overview.directions.col.later'), sortValue: (p) => p.later, render: (p) => <span className="num">{p.later}</span> },
      {
        key: 'nextTask',
        title: t('overview.directions.col.nextTask'),
        wrap: true,
        render: (p) =>
          p.nextTask ? (
            <>
              <Link to={`/tasks/${p.nextTask.id}`}>{p.nextTask.key}</Link> {p.nextTask.title}
            </>
          ) : (
            <span className="muted">—</span>
          ),
      },
    ],
    [],
  );

  return (
    <section>
      <div className="row between">
        <h2 style={{ margin: 0 }}>{t('overview.directions.title')}</h2>
        {canManage && (
          <button className="btn secondary" onClick={() => onSettings('new')}>
            {t('overview.newBusiness')}
          </button>
        )}
      </div>
      <p className="muted">{t('overview.directions.hint')}</p>
      <div className="stack">
        {data.directions.map((d) => (
          <div className="card" key={d.business.id}>
            <div className="row between">
              <b>
                {d.business.name} {d.business.status !== 'active' && <span className="badge warn">{statusLabel(d.business.status)}</span>}
              </b>
              <span className="row">
                {canWriteTasks && <AddProject businessId={d.business.id} onAdded={onChanged} />}
                <Link to={`/tasks?businessId=${d.business.id}`} className="btn secondary small">
                  {t('overview.tasksBtn')}
                </Link>
                {canManage && (
                  <button type="button" className="btn secondary small" onClick={() => onSettings(d.business.id)}>
                    {t('overview.settingsBtn')}
                  </button>
                )}
              </span>
            </div>
            <div style={{ marginTop: 'var(--sp-3)' }}>
              <Table columns={columns} rows={d.projects} rowKey={(p) => p.id ?? 'none'} empty={t('overview.projects.none')} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AddProject({ businessId, onAdded }: { businessId: string; onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!open)
    return (
      <button type="button" className="btn secondary small" onClick={() => setOpen(true)}>
        {t('overview.projects.add')}
      </button>
    );
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await businessesApi.createProject(businessId, name.trim());
      setName('');
      setOpen(false);
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="row" onSubmit={submit}>
      <input className="input" style={{ width: 180 }} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('overview.projects.name')} required autoFocus />
      <button className="btn small" type="submit" disabled={busy}>{t('common.save')}</button>
      <button className="btn secondary small" type="button" onClick={() => setOpen(false)}>{t('common.cancel')}</button>
      {error && <span className="error">{error}</span>}
    </form>
  );
}

function BusinessForm({ business, onSaved, onCancel }: { business: BusinessListItem | null; onSaved: () => void; onCancel: () => void }) {
  const [name, setName] = useState(business?.name ?? '');
  const [slug, setSlug] = useState(business?.slug ?? '');
  const [taskPrefix, setTaskPrefix] = useState(business?.taskPrefix ?? '');
  const [language, setLanguage] = useState(business?.language ?? 'uk');
  const [currency, setCurrency] = useState(business?.currency ?? 'EUR');
  const [timezone, setTimezone] = useState(business?.timezone ?? 'Europe/Lisbon');
  const [humans, setHumans] = useState<TeamMember[]>([]);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [initialMemberIds, setInitialMemberIds] = useState<string[]>([]);
  // Set right after a successful create, so a retry (e.g. setMembers failed) updates
  // instead of creating again with the same slug (which would hit slug_taken).
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.teamMembers().then((all) => setHumans(all.filter((m) => m.type === 'human'))).catch(() => undefined);
    if (business) {
      businessesApi.members(business.id).then((m) => {
        const ids = m.map((x) => x.actorId);
        setMemberIds(ids);
        setInitialMemberIds(ids);
      }).catch(() => undefined);
    }
  }, [business]);

  const toggleMember = (actorId: string) => setMemberIds((ids) => (ids.includes(actorId) ? ids.filter((x) => x !== actorId) : [...ids, actorId]));

  const sameMembers = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((id, i) => id === sortedB[i]);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const fields = { name, slug, taskPrefix: taskPrefix || undefined, language, currency, timezone };
      const targetId = business?.id ?? createdId;
      const saved = targetId ? await businessesApi.update(targetId, fields) : await businessesApi.create(fields);
      if (!targetId) setCreatedId(saved.id);
      if (!sameMembers(memberIds, initialMemberIds)) {
        await businessesApi.setMembers(saved.id, memberIds);
        setInitialMemberIds(memberIds);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async () => {
    if (!business) return;
    setBusy(true);
    setError(null);
    try {
      await businessesApi.update(business.id, { status: business.status === 'active' ? 'paused' : 'active' });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="form card" style={{ maxWidth: 520 }} onSubmit={submit}>
      <label>
        {t('overview.settings.name')}
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
      </label>
      <div className="row">
        <label style={{ flex: 1 }}>
          {t('overview.settings.slug')}
          <input className="input" value={slug} onChange={(e) => setSlug(e.target.value)} required maxLength={60} pattern="[a-z0-9-]+" />
        </label>
        <label style={{ flex: 1 }}>
          {t('overview.settings.taskPrefix')}
          <input className="input" value={taskPrefix} onChange={(e) => setTaskPrefix(e.target.value.toUpperCase())} maxLength={5} pattern="[A-Z]{2,5}" placeholder="OV" />
        </label>
      </div>
      <div className="row">
        <label style={{ flex: 1 }}>
          {t('overview.settings.language')}
          <select value={language} onChange={(e) => setLanguage(e.target.value)}>
            {LANGUAGES.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </label>
        <label style={{ flex: 1 }}>
          {t('overview.settings.currency')}
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label style={{ flex: 1 }}>
          {t('overview.settings.timezone')}
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {(TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES]).map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="field">
        {t('overview.settings.members')}
        <div className="checks">
          {humans.map((m) => (
            <label key={m.actorId}>
              <input type="checkbox" checked={memberIds.includes(m.actorId)} onChange={() => toggleMember(m.actorId)} />
              {m.displayName}
            </label>
          ))}
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      <div className="row">
        <button className="btn" type="submit" disabled={busy}>{t('common.save')}</button>
        <button className="btn secondary" type="button" onClick={onCancel} disabled={busy}>{t('common.cancel')}</button>
        {business && (
          <button className="btn secondary" type="button" onClick={toggleStatus} disabled={busy}>
            {t(business.status === 'active' ? 'overview.settings.pause' : 'overview.settings.activate')}
          </button>
        )}
      </div>
    </form>
  );
}
